import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// components/segmented-control.tsx
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { classNames } from '../utils';
import Button from './Button';
import { Calendar } from './Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
export default function AnalyticsSelector({ value, onValueChange, options, className, customDateRange, onCustomDateRangeChange, }) {
    const [tempRange, setTempRange] = React.useState({});
    const [calendarOpen, setCalendarOpen] = React.useState(false);
    // Initialize tempRange with current customDateRange when calendar opens
    React.useEffect(() => {
        if (calendarOpen && customDateRange) {
            setTempRange({
                from: customDateRange.from,
                to: customDateRange.to,
            });
        }
        else if (!calendarOpen) {
            setTempRange({});
        }
    }, [calendarOpen, customDateRange]);
    // Format date range for display
    const formatDateRange = (range) => {
        const formatDate = (date) => {
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            if (date.toDateString() === today.toDateString()) {
                return 'Today';
            }
            if (date.toDateString() === yesterday.toDateString()) {
                return 'Yesterday';
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
            });
        };
        if (range.from.toDateString() === range.to.toDateString()) {
            return formatDate(range.from);
        }
        return `${formatDate(range.from)} - ${formatDate(range.to)}`;
    };
    const handleApplyRange = () => {
        if (tempRange.from && tempRange.to && onCustomDateRangeChange) {
            onCustomDateRangeChange({
                from: tempRange.from,
                to: tempRange.to,
            });
            setCalendarOpen(false);
            onValueChange('custom');
        }
    };
    const handleValueChange = (newValue) => {
        if (newValue &&
            newValue !== 'custom' &&
            customDateRange &&
            onCustomDateRangeChange) {
            // Clear custom date range when selecting other options
            onCustomDateRangeChange(undefined);
        }
        onValueChange(newValue);
    };
    // Determine the effective value - if there's a custom date range, force value to 'custom'
    const effectiveValue = customDateRange && value !== 'custom' ? 'custom' : value;
    const getCustomLabel = () => {
        if (customDateRange) {
            return formatDateRange(customDateRange);
        }
        return (_jsxs("span", { className: "font-inter inline-flex items-center gap-1", children: [_jsx(CalendarIcon, { className: "size-4" }), "Custom"] }));
    };
    return (_jsx(ToggleGroup.Root, { type: "single", value: effectiveValue, onValueChange: (v) => v && handleValueChange(v), className: classNames('bg-muted bg-base-100 inline-flex items-center gap-0.5 rounded-lg p-0.5 text-sm', className), children: options.map((opt, index) => {
            var _a;
            return (_jsxs(React.Fragment, { children: [opt.value === 'custom' ? (_jsxs(Popover, { open: calendarOpen, onOpenChange: setCalendarOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx(ToggleGroup.Item, { value: opt.value, "data-state": effectiveValue === 'custom' ? 'on' : 'off', className: classNames(
                                    // base
                                    'text-base-500 h-[28px] w-auto rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none', 'hover:text-foreground', 
                                    // selected - ensure custom shows selected state when customDateRange exists
                                    effectiveValue === 'custom'
                                        ? 'text-foreground text-base-900 shadow-tabs rounded-lg bg-white'
                                        : 'data-[state=on]:text-foreground data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white', 
                                    // focus ring
                                    'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2', opt.className), children: getCustomLabel() }) }), _jsxs(PopoverContent, { className: "w-auto rounded-2xl bg-white p-4 px-3", align: "end", children: [_jsx(Calendar, { mode: "range", selected: tempRange.from && tempRange.to
                                            ? { from: tempRange.from, to: tempRange.to }
                                            : customDateRange
                                                ? { from: customDateRange.from, to: customDateRange.to }
                                                : undefined, onSelect: (range) => {
                                            setTempRange(range || {});
                                        }, numberOfMonths: 1 }), _jsx("div", { className: "border-base-200 mt-3 border-t py-3 pb-0", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "text-base-500 text-sm font-normal tracking-[0.015em]", children: tempRange.from && tempRange.to ? (_jsxs("span", { className: "text-base-500", children: ["Selected:", _jsxs("span", { className: "text-base-900", children: [' ', `${Math.ceil(Math.abs(tempRange.to.getTime() - tempRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1} days`] })] })) : ('Select dates') }), _jsx("div", { className: "flex gap-2", children: _jsx(Button, { size: "sm", onClick: handleApplyRange, disabled: !tempRange.from || !tempRange.to, className: "h-8 rounded-lg border-0 bg-[#795DFC] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#6B4FE8]", children: "Apply" }) })] }) })] })] })) : (_jsx(ToggleGroup.Item, { value: opt.value, className: classNames(
                        // base
                        'text-base-500 font-inter h-[28px] w-auto rounded-lg px-2 py-1.5 text-[13px] leading-none font-medium transition outline-none', 'hover:text-foreground', 
                        // selected
                        'data-[state=on]:text-foreground data-[state=on]:text-base-900 data-[state=on]:shadow-tabs data-[state=on]:rounded-lg data-[state=on]:bg-white', 
                        // focus ring
                        'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2', opt.className), children: opt.label })), opt.value !== 'custom' &&
                        index < options.length - 1 &&
                        ((_a = options[index + 1]) === null || _a === void 0 ? void 0 : _a.value) === 'custom' && (_jsx("div", { className: "bg-base-300 mx-1 h-4 w-px self-center" }))] }, opt.value));
        }) }));
}
